<template>
  <Modal
    v-if="!isMobile || props.persistent"
    v-model="localValue"
    :name="props.name"
    :size="props.size"
    :persistent="props.persistent"
    :class-list="props.classList"
    :style-list="props.styleList"
    :z-index="props.zIndex"
    :to="props.to"
    :title="props.title"
    @closed="emit('closed')"
    @opened="emit('opened')"
  >
    <slot />
  </Modal>
  <DBottomSheet
    v-else
    :model-value="localValue"
    :z-index="props.zIndex"
    :title="props.title"
    :dragger="false"
    overlay
    hide-inactive
    :to="props.to"
    @update:model-value="onUpdateBottomSheetState"
  >
    <div
      :class="props.classList"
      :style="props.styleList"
    >
      <slot />
    </div>
    <template
      v-if="$slots.footer"
      #footer
    >
      <div
        :class="props.classList"
        :style="props.styleList"
      >
        <slot name="footer" />
      </div>
    </template>
  </DBottomSheet>
</template>
<script setup>
import { computed, onUnmounted, watch } from 'vue';
import { useId } from '../../hooks/useId';
import { useAppState } from '../../store/useAppState';
import { useModalStore } from '../../store/useModalStore';
import Modal from './modal.vue';
import { disableBodyScroll, enableBodyScroll } from '../../assets/js/utils/bodyScrollLock';
import { DBottomSheet } from '@ahml/domrfbank-ui/elements/DBottomSheet.js';

defineOptions({
  name: 'UiModalOrBottomSheet',
  compatConfig: {
    MODE: 3,
  },
});

const props = defineProps({
  title: {
    type: String
  },
  /** @deprecated */
  popupTitle: {
    type: String
  },
  name: {
    type: String,
    default: () => useId(),
  },
  /** @type {'big'|'small'|'full'} */
  size: {
    type: String,
    default: 'big',
  },
  /**
   * A number, or a string containing a number.
   * @typedef {Object} Action
   * @property {string} class - classname
   * @property {string} target - link target
   * @property {string} url - link href
   * @property {string} name - btn text | innerHtml
   * @property {boolean} close - close on click
   */
  /**
   * @type {Action[]}
   */
  action: {
    type: Array,
    default: () => {
      return []
    }
  },
  modelValue: Boolean,
  dark: Boolean,
  zIndex: Number,
  styleList: Object,
  classList: [Object, String, Array],
  to: {
    type: String,
    default: '#app',
  },
  persistent: Boolean,
});

const emit = defineEmits(['close', 'open', 'update:modelValue', 'closed', 'opened']);

const { isMobile } = useAppState();

const modalStore = useModalStore();
modalStore.addModal({ name: props.name, value: props.modelValue });
onUnmounted(() => {
  modalStore.removeModal(props.name);
});

const localValue = computed({
  get: () => modalStore.modals.value[props.name],
  set: (val) => {
    if (val) {
      modalStore.openModal(props.name);
    } else {
      modalStore.closeModal(props.name);
    }
  }
});

watch(() => props.modelValue, (val) => {
  if (val === localValue.value) return;

  localValue.value = val;
});

watch(localValue, (val) => {
  emit('update:modelValue', val);

  if (val) {
    disableBodyScroll();
  } else {
    enableBodyScroll();
  }
});

onUnmounted(() => {
  enableBodyScroll();
});

const onUpdateBottomSheetState = (val) => {
  if (val) {
    emit('opened');
  } else {
    emit('closed');
  }
  localValue.value = val;
};
</script>
